import { Button } from "../../core/Button.js";
import { Modal, ModalClose, ModalFooter } from "../../core/Modal.js";

export interface ChatRule {
  title: string;
  description: string;
}

/** The rules as numbered title-and-sentence pairs; the modal's body. */
export function ChatRuleList({ rules }: { rules: readonly ChatRule[] }) {
  return (
    <ol className="flex flex-col gap-4">
      {rules.map((rule, i) => (
        <li key={rule.title} className="flex gap-3">
          <span aria-hidden className="pt-px font-mono text-mono-price text-accent-text">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="flex flex-col gap-0.5">
            <span className="font-body text-body-b1-strong text-text-primary">{rule.title}</span>
            <span className="font-body text-body-b2 text-text-secondary">{rule.description}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

export interface ChatRulesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "Before you chat". */
  title: string;
  /** One line before the rules. */
  intro?: string;
  /** Each a short title and one sentence, in the store's voice. */
  rules: readonly ChatRule[];
  /** "I agree, let me chat". */
  agreeLabel?: string;
  /** "Not now"; read-only, the one button that closes it ("Got it"). */
  dismissLabel: string;
  /** Asks to agree. Left out, the rules are a plain read, closable any way a modal is. */
  onAgree?: () => void;
}

/**
 * The room's rules, the first time someone chats: read, agree, then the composer opens. Without
 * `onAgree`, the same rules to read again later.
 */
export function ChatRulesModal({
  open,
  onOpenChange,
  title,
  intro,
  rules,
  agreeLabel,
  dismissLabel,
  onAgree,
}: ChatRulesModalProps) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={intro}
      required={onAgree !== undefined}
      size="sm"
      footer={
        <ModalFooter className="border-t-0 pt-0">
          {onAgree ? (
            <>
              <ModalClose render={<Button variant="ghost" />}>{dismissLabel}</ModalClose>
              <Button onClick={onAgree}>{agreeLabel}</Button>
            </>
          ) : (
            <ModalClose render={<Button />}>{dismissLabel}</ModalClose>
          )}
        </ModalFooter>
      }
    >
      <ChatRuleList rules={rules} />
    </Modal>
  );
}
